import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formatCurrency, formatDate, formatDateTime } from "../../utils/helpers";

const getAccessConfig = () => {
  if (!localStorage.user) {
    return { headers: {} };
  }

  const parsedUser = JSON.parse(localStorage.user);

  return {
    headers: {
      Authorization: `Bearer ${parsedUser.access_token}`,
    },
  };
};

const getStatusClasses = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (
    normalizedStatus.includes("approved") ||
    normalizedStatus.includes("verified") ||
    normalizedStatus.includes("active") ||
    normalizedStatus.includes("success")
  ) {
    return "bg-green-100 text-green-800";
  }

  if (
    normalizedStatus.includes("pending") ||
    normalizedStatus.includes("manual") ||
    normalizedStatus.includes("review")
  ) {
    return "bg-amber-100 text-amber-800";
  }

  if (
    normalizedStatus.includes("reject") ||
    normalizedStatus.includes("failed") ||
    normalizedStatus.includes("suspend") ||
    normalizedStatus.includes("cancel")
  ) {
    return "bg-red-100 text-red-800";
  }

  if (
    normalizedStatus.includes("transfer") ||
    normalizedStatus.includes("checkin") ||
    normalizedStatus.includes("checked")
  ) {
    return "bg-blue-100 text-blue-800";
  }

  return "bg-gray-100 text-gray-700";
};


const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(
      status
    )}`}
  >
    {status || "N/A"}
  </span>
);

const MetricCard = ({ label, value, hint, icon: Icon }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
        {hint ? <p className="mt-1 text-sm text-gray-500">{hint}</p> : null}
      </div>
      {Icon ? (
        <div className="rounded-lg bg-gray-50 p-2">
          <Icon className="h-5 w-5 text-gray-600" />
        </div>
      ) : null}
    </div>
  </div>
);

const FieldRow = ({ label, value, badge }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <div className="mt-2">
      {badge ? (
        <StatusBadge status={value} />
      ) : (
        <p className="break-words text-sm font-medium text-gray-900">
          {value === 0 ? "0" : value || "N/A"}
        </p>
      )}
    </div>
  </div>
);

const EmptyPanel = ({ title, description }) => (
  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
    <p className="text-sm font-semibold text-gray-700">{title}</p>
    <p className="mt-1 text-sm text-gray-500">{description}</p>
  </div>
);

const AuditLogDetail = () => {
  const { eventID, delegateProfileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [auditLog, setAuditLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const profileFromList = location.state?.profile;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchAuditLog = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await axios.get(`${window.$domain}/api/method/audit-log`, {
          ...getAccessConfig(),
          params: {
            delegate_profile_name: delegateProfileId,
          },
        });

        setAuditLog(response?.data?.message || response?.data?.data || null);
      } catch (fetchError) {
        console.log(fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (delegateProfileId) {
      fetchAuditLog();
    }
  }, [delegateProfileId]);

  if (localStorage.role !== "Operations Member") {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6">
        <h2 className="text-xl font-semibold text-red-900">Restricted Page</h2>
        <p className="mt-2 text-sm text-red-700">
          Audit Log is only available for the Operations Member role.
        </p>
      </div>
    );
  }

  const eventRegistrations = auditLog?.event_registrations || [];
  const transferRequests = auditLog?.conference_transfer_requests || [];
  const delegateDetails = auditLog?.delegate_profile_details || {};

  const totalSelfCheckins = eventRegistrations.reduce(
    (count, registration) => count + (registration?.self_checkins?.length || 0),
    0
  );

  const totalEventCheckins = eventRegistrations.reduce(
    (count, registration) => count + (registration?.event_checkins?.length || 0),
    0
  );

  const registrationStatuses = [...new Set(
    eventRegistrations
      .map((registration) => registration?.reg_status)
      .filter(Boolean)
  )];

  const displayContactNumber = profileFromList?.contactNumber || "N/A";

  const displayName =
    profileFromList?.fullName ||
    delegateDetails?.email ||
    auditLog?.delegate_profile ||
    delegateProfileId;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b border-gray-200 bg-white shadow-sm">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
          <div className="h-48 animate-pulse rounded-2xl bg-gray-200" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-32 animate-pulse rounded-2xl bg-gray-200" />
            ))}
          </div>
          <div className="h-96 animate-pulse rounded-2xl bg-gray-200" />
        </div>
      </div>
    );
  }

  if (error || !auditLog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b border-gray-200 bg-white shadow-sm">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-red-100 bg-white p-10 shadow-sm">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-2xl font-semibold text-gray-900">
              Unable to load audit log
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              The audit details for this delegate profile could not be fetched.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <div className="hidden h-10 w-px bg-gray-200 lg:block" />
              <div>
                <p className="text-sm font-medium text-blue-600">Audit Review</p>
                <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Delegate Profile: {auditLog?.delegate_profile || delegateProfileId}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={auditLog?.status} />
              {registrationStatuses.map((status) => (
                <span
                  key={status}
                  className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                >
                  Registration: {status}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Event Registrations"
            value={eventRegistrations.length}
            hint="Linked registrations found"
            icon={RectangleStackIcon}
          />
          <MetricCard
            label="Self Check-ins"
            value={totalSelfCheckins}
            hint="Across all registrations"
            icon={ClockIcon}
          />
          <MetricCard
            label="Event Check-ins"
            value={totalEventCheckins}
            hint="Across all registrations"
            icon={CheckBadgeIcon}
          />
          <MetricCard
            label="Transfer Requests"
            value={transferRequests.length}
            hint="Historical transfer records"
            icon={ArrowsRightLeftIcon}
          />
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-medium text-blue-100">
                  Delegate Snapshot
                </p>
                <h2 className="mt-1 text-3xl font-bold text-white">
                  {displayName}
                </h2>
                <p className="mt-2 text-sm text-blue-50">
                  Quick identity and verification context for audit review.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
            <FieldRow
              label="Email"
              value={delegateDetails?.email || profileFromList?.email}
            />
            <FieldRow label="Contact" value={displayContactNumber} />
            <FieldRow label="Nationality" value={delegateDetails?.nationality} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Registration Journey
              </h2>
              <p className="text-sm text-gray-500">
                Review each event registration alongside linked self check-ins
                and on-ground event check-ins.
              </p>
            </div>
            <p className="text-sm font-semibold text-gray-600">
              Registrations: {eventRegistrations.length}
            </p>
          </div>

          {eventRegistrations.length === 0 ? (
            <EmptyPanel
              title="No event registrations found"
              description="This delegate profile has no linked event registrations in the audit response."
            />
          ) : (
            eventRegistrations.map((registration) => (
              <div
                key={registration.event_registration}
                className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200"
              >
                <div className="border-b border-gray-200 px-6 py-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {registration.event_name || registration.event || "Unnamed Event"}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Event Start Date: {formatDate(registration.event_start_date)}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={registration.reg_status} />
                      <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                        {registration?.self_checkins?.length || 0} self check-ins
                      </span>
                      <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                        {registration?.event_checkins?.length || 0} event check-ins
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 md:grid-cols-2 xl:grid-cols-3">
                  <FieldRow
                    label="Hotel Room Number"
                    value={registration?.hotel_room_number}
                  />
                  <FieldRow
                    label="Checked In By"
                    value={registration?.checkin_by}
                  />
                  <FieldRow
                    label="Check-in At"
                    value={formatDateTime(registration?.checkin_at)}
                  />
                </div>

                <div className="grid gap-6 p-6 xl:grid-cols-2">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Self Check-ins
                      </h4>
                      <span className="text-sm font-medium text-gray-500">
                        {registration?.self_checkins?.length || 0} records
                      </span>
                    </div>

                    {registration?.self_checkins?.length ? (
                      <div className="overflow-hidden rounded-xl border border-gray-200">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-stone-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Record
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Created
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Modified
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {registration.self_checkins.map((checkin) => (
                                <tr key={checkin.self_checkin}>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                                    {checkin.self_checkin}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    <StatusBadge status={checkin.checkin_status} />
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatDateTime(checkin.creation)}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatDateTime(checkin.modified)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <EmptyPanel
                        title="No self check-ins"
                        description="No self check-in record is linked to this registration."
                      />
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Event Check-ins
                      </h4>
                      <span className="text-sm font-medium text-gray-500">
                        {registration?.event_checkins?.length || 0} records
                      </span>
                    </div>

                    {registration?.event_checkins?.length ? (
                      <div className="overflow-hidden rounded-xl border border-gray-200">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-stone-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Record
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Event
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Created
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Modified
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {registration.event_checkins.map((checkin) => (
                                <tr key={checkin.event_checkin}>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                                    {checkin.event_checkin}
                                  </td>
                                  <td className="px-4 py-4 text-sm text-gray-600">
                                    <div className="font-medium text-gray-900">
                                      {checkin.event_name || "N/A"}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {checkin.event || "N/A"}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    <StatusBadge status={checkin.checkin_status} />
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatDateTime(checkin.creation)}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatDateTime(checkin.modified)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <EmptyPanel
                        title="No event check-ins"
                        description="No on-ground event check-in record is linked to this registration."
                      />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="border-b border-gray-200 px-6 py-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Conference Transfer Requests
                </h2>
                <p className="text-sm text-gray-500">
                  Review historical transfer attempts, approvals, refund
                  amounts, and voucher references.
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-600">
                Requests: {transferRequests.length}
              </p>
            </div>
          </div>

          <div className="p-6">
            {transferRequests.length ? (
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-stone-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Request
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          From / To
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Approval
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Refund
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Voucher
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                          Modified
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {transferRequests.map((request) => (
                        <tr key={request.name}>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            <div className="font-medium text-gray-900">
                              {request.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Event Registration: {request.event_registration || "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              Created: {formatDateTime(request.creation)}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium text-gray-900">
                                {request.event_name || "N/A"}
                              </span>
                              <div className="text-xs text-gray-500">
                                {formatDate(request.event_start_date)}
                              </div>
                            </div>
                            <div className="my-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                              to
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">
                                {request.transfer_to_event_name || "N/A"}
                              </span>
                              <div className="text-xs text-gray-500">
                                {formatDate(request.transfer_to_event_start_date)}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                            <StatusBadge status={request.approval_status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                            {formatCurrency(request.refund_amount)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {request.voucher_code || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                            {formatDateTime(request.modified)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <EmptyPanel
                title="No transfer requests"
                description="This delegate profile has no conference transfer requests in the audit response."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogDetail;
